import React, { useEffect, useRef } from 'react';
import {
  Box,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { HeaderGroup, SortingRule, UseSortByColumnProps } from 'react-table';
import { visuallyHidden } from '@mui/utils';
import Checkbox from 'new/components/Checkbox';

type TableHeaderProps<D extends object> = {
  headerGroups: HeaderGroup<D>[];
  isAllRowsSelected: boolean;
  toggleAllRowsSelected: () => void;
  withRowSelect?: boolean;
  saveSortBy?: Array<SortingRule<D>>;
};

const TableHeader = <D extends object>({
  headerGroups,
  isAllRowsSelected,
  toggleAllRowsSelected,
  withRowSelect,
  saveSortBy,
}: TableHeaderProps<D>) => {
  const firstRender = useRef<Boolean>(true);
  const sortUpdated = useRef<Boolean>(false);

  if (saveSortBy && !firstRender.current && sortUpdated.current) {
    const sorted = headerGroups[0].headers.filter((header) => {
      if (header.isSorted) {
        return header;
      }
    });
    if (sorted.length > 0) {
      const requiredSort = [{ id: sorted[0].id, desc: sorted[0].isSortedDesc }];
      saveSortBy[0].id = requiredSort[0].id;
      saveSortBy[0].desc = requiredSort[0].desc;
    } else {
      saveSortBy[0].id = '';
    }
    sortUpdated.current = false;
  }
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    }
  }, []);

  return (
    <TableHead>
      {headerGroups.map((headerGroup) => (
        <TableRow {...headerGroup.getHeaderGroupProps()}>
          {withRowSelect && (
            <TableCell key="select" id="select" align="left">
              <Checkbox
                checked={isAllRowsSelected}
                onChange={() => toggleAllRowsSelected()}
              />
            </TableCell>
          )}
          {headerGroup.headers.map((column) => {
            const { isSorted, isSortedDesc, getSortByToggleProps } =
              column as unknown as UseSortByColumnProps<D>;
            let sortDirection: 'desc' | 'asc' | undefined;

            if (isSorted) {
              if (isSortedDesc) {
                sortDirection = 'desc';
              } else {
                sortDirection = 'asc';
              }
            }

            const isNotEmptyColumn =
              column.Header !== '' && typeof column.Header !== 'function';
            const disableSortBy = column.disableSortBy;

            return (
              <TableCell
                {...column.getHeaderProps(getSortByToggleProps())}
                key={column.id}
                id={column.id}
                sortDirection={sortDirection}
                align={column.align}
                onClick={(e) => {
                  if (column.canSort) {
                    getSortByToggleProps().onClick?.(e);
                  }
                  sortUpdated.current = true;
                }}
              >
                {column.Header}
                {!disableSortBy && (
                  <TableSortLabel
                    active={isSorted}
                    direction={sortDirection}
                    sx={{
                      visibility: isNotEmptyColumn ? 'visible' : 'hidden',
                      mb: '3px',
                    }}
                  >
                    <Box component="span" sx={visuallyHidden}>
                      {isSorted
                        ? isSortedDesc
                          ? 'sorted descending'
                          : 'sorted ascending'
                        : ''}
                    </Box>
                  </TableSortLabel>
                )}
              </TableCell>
            );
          })}
        </TableRow>
      ))}
    </TableHead>
  );
};

export default TableHeader;
